import { API_URL } from "@/config";

const SESSION_KEY = "xevon_session_id";
const EXPIRED_KEY = "xevon_session_expired";

export const getStoredSession = () => {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
};

export const storeSession = (id) => {
  try {
    if (id) localStorage.setItem(SESSION_KEY, id);
    else localStorage.removeItem(SESSION_KEY);
  } catch { /* storage unavailable */ }
};

// One-shot flag so the UI can say "session expired" instead of a plain login page.
export const consumeSessionExpired = () => {
  try {
    const flag = localStorage.getItem(EXPIRED_KEY);
    if (flag) localStorage.removeItem(EXPIRED_KEY);
    return !!flag;
  } catch {
    return false;
  }
};

// Capture the session id handed back in the URL fragment after Discord OAuth.
// The fragment is never sent to any server; we store it and clean the URL.
if (typeof window !== "undefined" && window.location.hash.startsWith("#session=")) {
  const id = window.location.hash.slice("#session=".length);
  if (id) storeSession(id);
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}

export class ApiError extends Error {
  constructor(message, { status = null, kind = "error" } = {}) {
    super(message);
    this.status = status;
    this.kind = kind;
  }
}

export async function apiRequest(path, { method = "GET", timeout = 10000, body } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  const sessionId = getStoredSession();
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method,
      credentials: "include",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(sessionId ? { "X-Session-Id": sessionId } : {}),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401 || res.status === 403) {
      if (res.status === 401 && sessionId) {
        try {
          localStorage.setItem(EXPIRED_KEY, "1");
        } catch { /* storage unavailable */ }
        storeSession(null);
      }
      const detail = await res.json().catch(() => null);
      const msg = typeof detail?.detail === "string" ? detail.detail : res.status === 403 ? "You don't have permission to manage this feature." : "Authentication required";
      throw new ApiError(msg, { status: res.status, kind: "auth" });
    }
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      const msg =
        typeof detail?.detail === "string"
          ? detail.detail
          : typeof detail?.detail?.message === "string"
            ? detail.detail.message
            : "Service returned an error";
      throw new ApiError(msg, { status: res.status, kind: "http" });
    }
    const data = await res.json().catch(() => null);
    if (data === null || data === undefined) {
      throw new ApiError("Empty response", { status: res.status, kind: "empty" });
    }
    return data;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err.name === "AbortError") {
      throw new ApiError("Request timed out", { kind: "timeout" });
    }
    throw new ApiError("Unable to connect to Xevon services", { kind: "offline" });
  } finally {
    clearTimeout(timer);
  }
}
